import { useState, useEffect } from 'react'
import { Heart, Mail, Phone, Clock, Building2 } from 'lucide-react'
import { config, useClientConfig } from '@workspace/public-utils'

interface CompanyInfo {
  name: string | null
  contact_email: string | null
  contact_number: string | null
  contact_number_secondary: string | null
  working_hours: string | null
  address: string | null
  business_registration_number?: string | null
  ecommerce_registration_number?: string | null
  owner?: string | null
  chief_privacy_officer?: string | null
  bank_infos?: Array<{
    bank_name: string
    account_number: string
    account_holder: string
    bank_logo_url: string
  }>
  extra_bottom_images?: string[]
}

const Footer = () => {
  const clientConfig = useClientConfig()
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)

  useEffect(() => {
    fetchCompanyInfo()
  }, [])

  const fetchCompanyInfo = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/v1/public/company-info`)
      if (response.ok) {
        const data = await response.json()
        setCompanyInfo(data)
      }
    } catch (error) {
      console.error('Failed to fetch company info:', error)
    }
  }

  return (
    <footer className="bg-gradient-to-br from-gray-50 to-happiness-primary/5 border-t border-gray-200">
      {/* Bank Account Information Section - Full Height */}
      {companyInfo?.bank_infos && companyInfo.bank_infos.length > 0 && (
        <div className="bg-gradient-to-br from-purple-50/50 to-gray-50 py-16" data-testid="prefooter-bank-row">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col">
            <h3 className="text-2xl font-bold text-center mb-8 bg-gradient-to-r from-happiness-primary to-purple-600 bg-clip-text text-transparent">
              무통장 입금 계좌
            </h3>
            <div className="flex flex-wrap items-stretch justify-center gap-6 flex-1">
              {companyInfo.bank_infos
                .filter((b, index, self) => 
                  // Remove duplicates based on account number
                  index === self.findIndex((t) => t.account_number === b.account_number)
                )
                .map((b, i) => (
                <div key={`${b.account_number}-${i}`} className="p-8 bg-white rounded-2xl border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col items-center justify-center gap-6 min-w-[350px] min-h-[200px]" data-testid="bank-card">
                  {b.bank_logo_url && (
                    <img src={b.bank_logo_url} alt={b.bank_name} className="w-20 h-20 object-contain" />
                  )}
                  <div className="flex flex-col items-center text-center gap-2">
                    <span className="font-black text-gray-900 text-2xl">{b.bank_name}</span>
                    <span className="text-gray-700 font-bold text-xl tracking-wide">{b.account_number}</span>
                    <span className="text-gray-500 text-base mt-2">예금주: {b.account_holder}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Bottom logos with full-bleed layout */}
      {companyInfo?.extra_bottom_images && companyInfo.extra_bottom_images.length > 0 && (
        <div className="bg-white w-full py-8 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8 w-full" data-testid="bottom-logos-row">
            {companyInfo.extra_bottom_images.map((img, idx) => (
              <img key={idx} src={img} alt={`brand-${idx}`} className="h-12 md:h-14 lg:h-16 object-contain flex-shrink-0" data-testid="bottom-logo" />
            ))}
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg" style={{ backgroundColor: clientConfig.logoIconColor }}>
                <span className="text-white font-black text-xl">{clientConfig.logoIconText}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-2xl font-black" style={{ color: clientConfig.logoIconColor }}>{clientConfig.brandNameEn}</span>
                <span className="text-lg font-bold text-gray-800">{clientConfig.brandName}</span>
              </div>
            </div>
            <p className="text-gray-700 mb-4 leading-relaxed">
              AI 기반의 스마트한 복권 번호 생성과 과학적인 통계 분석을 통해<br />
              당신의 <span style={{ color: clientConfig.logoIconColor }} className="font-semibold">성공적인 미래</span>를 함께 만들어 갑니다.
            </p>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Heart className="w-4 h-4 text-happiness-primary" />
              <span>대한민국 1등 온라인 복권 서비스</span>
            </div>
          </div>

          {/* Services */}
          <div className="col-span-1">
            <h3 className="font-bold text-gray-900 mb-4 text-lg">서비스</h3>
            <ul className="space-y-3">
              <li>
                <a href="/results" className="text-gray-600 hover:text-happiness-primary text-sm transition-colors flex items-center">
                  <span className="w-2 h-2 bg-happiness-primary/60 rounded-full mr-2"></span>
                  당첨결과 조회
                </a>
              </li>
              <li>
                <a href="/analysis" className="text-gray-600 hover:text-happiness-primary text-sm transition-colors flex items-center">
                  <span className="w-2 h-2 bg-happiness-primary/60 rounded-full mr-2"></span>
                  번호 분석
                </a>
              </li>
              <li>
                <a href="/plans" className="text-gray-600 hover:text-happiness-primary text-sm transition-colors flex items-center">
                  <span className="w-2 h-2 bg-happiness-primary/60 rounded-full mr-2"></span>
                  구독 플랜
                </a>
              </li>
              <li>
                <a href="/dashboard" className="text-gray-600 hover:text-happiness-primary text-sm transition-colors flex items-center">
                  <span className="w-2 h-2 bg-happiness-primary/60 rounded-full mr-2"></span>
                  마이페이지
                </a>
              </li>
            </ul>
          </div>

          {/* Support & Contact */}
          <div className="col-span-1">
            <h3 className="font-bold text-gray-900 mb-4 text-lg">고객지원</h3>
            <ul className="space-y-3 mb-6">
              <li>
                <a href="/faq" className="text-gray-600 hover:text-happiness-primary text-sm transition-colors flex items-center">
                  <span className="w-2 h-2 bg-happiness-secondary/60 rounded-full mr-2"></span>
                  자주 묻는 질문
                </a>
              </li>
              <li>
                <a href="/contact" className="text-gray-600 hover:text-happiness-primary text-sm transition-colors flex items-center">
                  <span className="w-2 h-2 bg-happiness-secondary/60 rounded-full mr-2"></span>
                  문의하기
                </a>
              </li>
            </ul>

            {/* Contact Info - Dynamic from Backend */}
            <div className="space-y-2">
              {companyInfo?.contact_email && (
                <div className="flex items-center space-x-2 text-gray-600 text-sm">
                  <Mail className="w-4 h-4 text-happiness-primary" />
                  <span>{companyInfo.contact_email}</span>
                </div>
              )}
              {companyInfo?.contact_number && (
                <div className="flex items-center space-x-2 text-gray-600 text-sm">
                  <Phone className="w-4 h-4 text-happiness-primary" />
                  <span>{companyInfo.contact_number}</span>
                </div>
              )}
              {companyInfo?.contact_number_secondary && (
                <div className="flex items-center space-x-2 text-gray-600 text-sm">
                  <Phone className="w-4 h-4 text-happiness-primary" />
                  <span>고객센터 연락처: {companyInfo.contact_number_secondary}</span>
                </div>
              )}
              {companyInfo?.working_hours && (
                <div className="flex items-center space-x-2 text-gray-600 text-sm">
                  <Clock className="w-4 h-4 text-happiness-primary" />
                  <span>{companyInfo.working_hours}</span>
                </div>
              )}
              {companyInfo?.address && (
                <div className="flex items-center space-x-2 text-gray-600 text-sm">
                  <Building2 className="w-4 h-4 text-happiness-primary" />
                  <span>{companyInfo.address}</span>
                </div>
              )}
              {/* Legal/Company Info */}
              {(companyInfo?.business_registration_number || companyInfo?.ecommerce_registration_number || companyInfo?.owner || companyInfo?.chief_privacy_officer) && (
                <div className="mt-3 space-y-1 text-gray-500 text-xs">
                  {companyInfo?.business_registration_number && (
                    <div>사업자번호: {companyInfo.business_registration_number}</div>
                  )}
                  {companyInfo?.ecommerce_registration_number && (
                    <div>통신판매신고번호: {companyInfo.ecommerce_registration_number}</div>
                  )}
                  {companyInfo?.owner && (
                    <div>대표자: {companyInfo.owner}</div>
                  )}
                  {companyInfo?.chief_privacy_officer && (
                    <div>개인정보책임자: {companyInfo.chief_privacy_officer}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-300 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-center md:text-left">
              <p className="text-gray-600 text-sm font-medium mb-1">
                © 2024 {companyInfo?.name || clientConfig.copyrightFallback}. All rights reserved.
              </p>
              <p className="text-gray-500 text-xs">
                본 서비스는 게임 중독 예방을 위해 만 19세 이상만 이용 가능합니다.
              </p>
            </div>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <div className="text-xs text-gray-500">
                안전한 복권 문화를 위해 노력합니다
              </div>
              <div className="w-8 h-8 bg-gradient-to-br from-happiness-primary to-happiness-secondary rounded-full flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
